import { GetSessionSharingInfoInput, GetSessionSharingInfoOutput } from '@creative-kit/shared';
import { auth, fn } from 'firebase-modules';
import { FirebaseFunction } from './utils';

const callable = fn.httpsCallable('getSessionSharingInfo');

/**
 * Fetches session owner info via same-origin proxy to avoid CORS when running
 * on localhost. Falls back to direct callable when proxy is not available (e.g. production).
 */
async function getSessionSharingInfoImpl(
  input: GetSessionSharingInfoInput,
): Promise<{ data: GetSessionSharingInfoOutput }> {
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  try {
    const token = await auth.currentUser?.getIdToken();
    const url = `${window.location.origin}/api/getSessionSharingInfo`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ data: input }),
    });
    if (res.status === 404) {
      if (isLocalhost) {
        throw new Error('Session sharing info unavailable (proxy not configured for this port).');
      }
      return callable(input);
    }
    const json = await res.json().catch(() => ({}));
    if (json?.error) {
      const err = json.error as { code?: string; message?: string };
      throw new Error(err.message || err.code || 'getSessionSharingInfo failed');
    }
    if (!res.ok) {
      throw new Error(
        (json as { error?: { message?: string } })?.error?.message ||
          `Request failed (${res.status})`,
      );
    }
    return { data: json.result as GetSessionSharingInfoOutput };
  } catch (e) {
    // On localhost, never fall back to callable (avoids CORS). Proxy must be used.
    if (
      !isLocalhost &&
      e instanceof TypeError &&
      (e.message === 'Failed to fetch' || (e as Error).message?.includes('network'))
    ) {
      return callable(input);
    }
    throw e;
  }
}

export const getSessionSharingInfo: FirebaseFunction<
  GetSessionSharingInfoInput,
  GetSessionSharingInfoOutput
> = getSessionSharingInfoImpl;
